import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import CloseIcon from '@material-ui/icons/Close'
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined'
import ImageIcon from '@material-ui/icons/Image'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tabs,
  Tab,
  Button,
  Dropzone,
  DEFAULT_FILE_ACCEPT,
  IconButton,
  ListItem,
  ListItemText,
  SearchBar,
  Spinner,
  FilePreview,
  useDebounce,
  makeStyles,
  imageCrossOrigin,
  retryCorsImageOnError,
  isPdfFile,
  useTranslation
} from '@paintscout/ui'
import type { DialogProps, MuiTheme as Theme } from '@paintscout/ui'
import type { WithClientOptions, WithUser } from '@paintscout/ui-business'
import { withClientOptions, withUser } from '@paintscout/ui-business'
import { getFeature, hasIntegrationInterface } from '@paintscout/util/builder'
import { removeAuth0Prefix } from '@paintscout/util/users'
import { HEIC_FILE_TYPES } from '@paintscout/util/s3'
import { useSnackbar } from 'notistack'
import type {
  AddFileDialogTab,
  CompanyCamApiPhoto,
  CompanyCamPhotoFile,
  CompanyCamPhotoTab,
  CompanyCamProject
} from './AddFileDialog.helpers'
import {
  buildCompanyCamPhotoFiles,
  isAddFileDialogTab,
  isCompanyCamPhotoTab,
  orderPickedPhotos,
  readLastCcProject,
  readLastPhotoTab,
  readLastTab,
  writeLastCcProject,
  writeLastPhotoTab,
  writeLastTab
} from './AddFileDialog.helpers'
import { useCompanyQuery, useContactQuery, useDealQuery } from '@paintscout/api'
import type { Company, Contact, Deal } from '@paintscout/api'
import type { QuoteFile } from 'paintscout'
import { useMutation } from '@apollo/react-hooks'
import { gql } from 'apollo-boost'

export type { AddFileDialogTab } from './AddFileDialog.helpers'

export interface AddFileDialogProps extends Omit<DialogProps, 'onClose' | 'title'> {
  /** Title shown at the top. Defaults to 'Add File'. */
  title?: string
  /** Optional subtitle under the title (e.g. 'Filter Primary'). */
  subtitle?: string

  /** Max file size for upload, in bytes. Defaults to 100MB. */
  maxFileSize?: number
  /** Accepted MIME types for upload. Defaults to images + PDF. */
  accept?: string

  /** Optional doc whose attached files populate the 'Deal Files' tab. */
  fileDocId?: string
  fileDocType?: 'contact' | 'company' | 'deal'
  /**
   * Scopes the remembered CompanyCam project to a single document — pass the
   * quote `_id` (estimate flows) or the deal `_id` (deal flows). With it set,
   * re-opening the picker on the same doc jumps back to its last project, while
   * a different (or new) doc opens the general project search. Omit it (note
   * flows) to fall back to the legacy per-user memory.
   */
  scopeId?: string
  /**
   * Pre-fetched files to populate the source tab directly, instead of querying a
   * single doc via fileDocId/fileDocType. Used to pull from linked estimates into
   * a deal (the reverse of the deal -> estimate flow). When provided, takes
   * precedence over the queried doc files.
   */
  sourceFiles?: QuoteFile[]
  /** Override the 'Deal Files' tab label (defaults to 'Deal Files'). */
  dealFilesLabel?: string

  /** Tab to open initially. Defaults to 'upload'. */
  initialTab?: AddFileDialogTab

  /** Called with raw File objects when the user uploads via the Upload tab. */
  onUpload?: (files: File[]) => void

  /**
   * Called with existing QuoteFiles when the user picks from a source tab
   * (Deal Files or Company Cam). Company Cam files carry `provider: 'companycam'`
   * so the editor can disable in-app image editing for live remote URLs.
   */
  onSelectFiles?: (files: QuoteFile[]) => void

  /** Close handler — fires on X click, Cancel button, or backdrop dismissal. */
  onCancel?: () => void
}

const PROCESS_EVENT = gql`
  mutation ($type: String!, $provider: String!, $async: Boolean, $params: JSON) {
    processEvent(type: $type, provider: $provider, async: $async, params: $params) {
      provider
      result
      error
    }
  }
`

type AddFileDialogPropsWithInjections = AddFileDialogProps & WithClientOptions & WithUser

const DEFAULT_MAX_FILE_SIZE = 1024 * 1024 * 100 // 100MB
const DEFAULT_ACCEPT = DEFAULT_FILE_ACCEPT

const useStyles = makeStyles<Theme>((theme) => ({
  paper: {
    height: 'calc(100vh - 96px)',
    maxHeight: 720,
    width: '100%',
    maxWidth: 720,
    [theme.breakpoints.down('sm')]: {
      height: '100%',
      maxHeight: '100%',
      maxWidth: '100%'
    }
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`
  },
  titleStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25)
  },
  titleSubtitle: {
    ...theme.typography.caption,
    color: theme.paintscout.palette.brand.Neutral.P50
  },
  content: {
    padding: 0,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  tabsRow: {
    position: 'relative',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    // Draw the bottom divider as a 2px bar at bottom:0 — the same band the MUI active-tab
    // indicator occupies (height:2, bottom:0) — so the blue underline sits exactly on top
    // of the divider instead of floating above a 1px border (#21267). Mirrors the
    // NewFormSection tabs pattern used by the Availability settings tabs.
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: '2px',
      background: theme.paintscout.palette.brand.Neutral.P90
    },
    // Raise the indicator span above the ::after divider so the active tab's blue line
    // wins where the two overlap.
    '& div > span': {
      zIndex: 2
    }
  },
  // Force each tab to fill the 48px tab row. The @paintscout/ui Tab (CustomizedTab) sets
  // minHeight:0, so tabs are only as tall as their content and the @paintscout/ui Tabs root
  // (align-items:center) centers that short scroller inside MUI's min-height:48 row, leaving
  // a gap below the scroller that floats the indicator above the divider. minHeight:48 fills
  // the row (as the Availability tabs / NewFormSection do) so the indicator drops onto the
  // ::after divider (#21267).
  tab: {
    minHeight: 48
  },
  tabLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    textTransform: 'none'
  },
  availabilityDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: theme.palette.success?.main ?? '#22c55e',
    display: 'inline-block'
  },
  panel: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column'
  },
  dropzoneRoot: {
    flex: 1,
    minHeight: 240,
    border: `1.5px dashed ${theme.paintscout.palette.brand.Neutral.P85}`,
    borderRadius: theme.borderRadius.md,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: theme.paintscout.palette.brand.Neutral.P98 ?? theme.palette.common.white,
    color: theme.paintscout.palette.brand.Neutral.P50,
    cursor: 'pointer',
    transition: 'border-color 120ms ease, background-color 120ms ease',
    '&:hover': {
      borderColor: theme.palette.primary.main
    }
  },
  dropzonePreview: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  dropzoneIcon: {
    fontSize: 36,
    color: theme.paintscout.palette.brand.Neutral.P50
  },
  dropzoneText: {
    ...theme.typography.body2,
    color: theme.paintscout.palette.brand.Neutral.P30
  },
  fileMeta: {
    ...theme.typography.caption,
    color: theme.paintscout.palette.brand.Neutral.P50,
    marginTop: theme.spacing(1)
  },
  filesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
    gap: theme.spacing(1.5),
    flex: 1,
    alignContent: 'start'
  },
  fileTile: {
    position: 'relative',
    // padding-top = width keeps tiles square; aspect-ratio is unreliable when
    // the percentage-height image feeds back into auto-sized grid rows
    height: 0,
    padding: '100% 0 0',
    border: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    borderRadius: theme.borderRadius.md,
    overflow: 'hidden',
    cursor: 'pointer',
    background: theme.paintscout.palette.brand.Neutral.P96,
    transition: 'border-color 120ms ease, box-shadow 120ms ease',
    '&:hover': {
      borderColor: theme.paintscout.palette.brand.Neutral.P70
    }
  },
  fileTileSelected: {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`
  },
  fileTileImg: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block'
  },
  tileFill: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  // Wrapper that lets a shared FilePreview fill the square tile so PDFs/videos
  // render a real thumbnail (first-page PNG / video frame) instead of bare
  // filename text (#21654).
  fileTilePreview: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden'
  },
  // Override FilePreview root's default 100px min-size + padding so it fills the tile.
  fileTilePreviewRoot: {
    width: '100%',
    height: '100%',
    minWidth: 0,
    minHeight: 0,
    padding: 0
  },
  emptyState: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.paintscout.palette.brand.Neutral.P50,
    textAlign: 'center',
    padding: theme.spacing(4)
  },
  ccPanel: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2)
  },
  ccSearch: {
    marginBottom: theme.spacing(1)
  },
  ccSearchBarRoot: {
    marginTop: 0
  },
  ccSearchBarPaper: {
    padding: `8px 16px`
  },
  ccProjectList: {
    flex: 1,
    overflow: 'auto'
  },
  ccProjectThumb: {
    width: 56,
    height: 56,
    objectFit: 'cover',
    borderRadius: theme.borderRadius.md,
    marginRight: theme.spacing(2),
    background: theme.paintscout.palette.brand.Neutral.P96
  },
  ccProjectThumbPlaceholder: {
    width: 56,
    height: 56,
    flexShrink: 0,
    borderRadius: theme.borderRadius.md,
    marginRight: theme.spacing(2),
    background: theme.paintscout.palette.brand.Neutral.P98,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  ccProjectThumbPlaceholderIcon: {
    fontSize: 32,
    color: theme.paintscout.palette.brand.Neutral.P90
  },
  ccProjectHeader: {
    marginBottom: theme.spacing(1)
  },
  ccProjectHeaderName: {
    ...theme.typography.body2,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.paintscout.palette.brand.Neutral.P40
  },
  ccProjectHeaderAddress: {
    ...theme.typography.caption,
    color: theme.paintscout.palette.brand.Neutral.P80
  },
  ccSubTabsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1)
  },
  ccPhotoGrid: {
    flex: 1,
    overflow: 'auto',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
    gap: theme.spacing(1.5),
    alignContent: 'start',
    // small padding so the selected tile's box-shadow ring isn't clipped at the scroll edges
    padding: theme.spacing(0.5)
  },
  ccPhotoGridEmpty: {
    // Span every grid column so the empty-state message fills the dialog width
    // and stays centered, instead of wrapping inside a single ~120px grid cell (ENG-726).
    gridColumn: '1 / -1'
  },
  ccPhotoTile: {
    position: 'relative',
    height: 0,
    padding: '100% 0 0',
    border: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    borderRadius: theme.borderRadius.md,
    overflow: 'hidden',
    cursor: 'pointer',
    background: theme.paintscout.palette.brand.Neutral.P96,
    transition: 'border-color 120ms ease, box-shadow 120ms ease',
    '&:hover': {
      borderColor: theme.paintscout.palette.brand.Neutral.P70
    }
  },
  ccPhotoTileSelected: {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 2px ${theme.palette.primary.main}`
  },
  ccPhotoImg: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block'
  },
  // Caption overlay on a picker tile — mirrors the FilesGridNext overlay so the
  // CompanyCam description is visible while selecting. The gradient + padding
  // live on this container; the clamp lives on the inner text span. Keeping the
  // padding OFF the -webkit-box element avoids the iOS Safari bug where bottom
  // padding makes the last clamped line render cut off mid-glyph (#21231).
  ccPhotoCaption: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    padding: theme.spacing(0.5, 0.75),
    color: theme.palette.common.white,
    background: 'linear-gradient(to top, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0) 100%)',
    // let clicks fall through to the tile button beneath
    pointerEvents: 'none'
  },
  ccPhotoCaptionText: {
    ...theme.typography.caption,
    lineHeight: 1.25,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden'
  },
  ccToolbarActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  }
}))

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) return `${Math.round(bytes / (1024 * 1024 * 1024))}GB`
  if (bytes >= 1024 * 1024) return `${Math.round(bytes / (1024 * 1024))}MB`
  if (bytes >= 1024) return `${Math.round(bytes / 1024)}KB`
  return `${bytes}B`
}

function AddFileDialog(props: AddFileDialogPropsWithInjections) {
  const {
    title,
    subtitle,
    maxFileSize = DEFAULT_MAX_FILE_SIZE,
    accept = DEFAULT_ACCEPT,
    fileDocId,
    fileDocType,
    scopeId,
    sourceFiles,
    dealFilesLabel,
    initialTab,
    onUpload,
    onSelectFiles,
    onCancel,
    clientOptions,
    user,
    ...dialogProps
  } = props

  const { __ } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const resolvedTitle = title ?? __('Add File')
  const resolvedDealFilesLabel = dealFilesLabel ?? __('Deal Files')

  const classes = useStyles({})
  const ccUserId = removeAuth0Prefix(user?.user?.user_id ?? '')

  // HEIC is only convertible via the S3 upload path (server-side heicConvert
  // task) — mirrors the same guard in uploadFile.ts. Blocking it here too gives
  // an immediate rejection in the picker instead of a round-trip failure.
  const s3ImageUploadEnabled = getFeature({ options: clientOptions.options, path: 's3ImageUpload.enabled' })

  const companyCamAvailable = useMemo(
    () => hasIntegrationInterface({ options: clientOptions.options, integration: 'companycam' }),
    [clientOptions.options]
  )
  const hasPrefetchedSource = !!sourceFiles
  const dealFilesAvailable = hasPrefetchedSource ? sourceFiles.length > 0 : !!fileDocId && !!fileDocType

  // An explicit `initialTab` (e.g. the CompanyCam swap/add-provider flows) wins;
  // otherwise reopen on the tab the user last used, falling back to Upload.
  const [currentTab, setCurrentTab] = useState<AddFileDialogTab>(() => initialTab ?? readLastTab(ccUserId) ?? 'upload')

  // Persist only user-initiated tab switches, so forced `initialTab` opens don't
  // pollute the remembered tab.
  const handleTabChange = useCallback(
    (tab: AddFileDialogTab) => {
      setCurrentTab(tab)
      writeLastTab(ccUserId, tab)
    },
    [ccUserId]
  )
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set())

  const { data: contactData } = useContactQuery({
    variables: { id: fileDocId },
    skip: hasPrefetchedSource || !dealFilesAvailable || fileDocType !== 'contact',
    fetchPolicy: 'cache-and-network'
  })
  const { data: companyData } = useCompanyQuery({
    variables: { id: fileDocId },
    skip: hasPrefetchedSource || !dealFilesAvailable || fileDocType !== 'company',
    fetchPolicy: 'cache-and-network'
  })
  const { data: dealData } = useDealQuery({
    variables: { id: fileDocId },
    skip: hasPrefetchedSource || !dealFilesAvailable || fileDocType !== 'deal',
    fetchPolicy: 'cache-and-network'
  })

  const sourceDoc: Contact | Company | Deal | null =
    (contactData?.contact as Contact) || (companyData?.company as Company) || (dealData?.deal as Deal) || null
  const docFiles: QuoteFile[] = useMemo(
    () => sourceFiles ?? (sourceDoc ? (Object.values(sourceDoc?.files ?? {}) as QuoteFile[]) : []),
    [sourceFiles, sourceDoc]
  )

  // If the initially-selected tab becomes unavailable as props change, fall back to Upload.
  useEffect(() => {
    if (currentTab === 'deal-files' && !dealFilesAvailable) setCurrentTab('upload')
    if (currentTab === 'company-cam' && !companyCamAvailable) setCurrentTab('upload')
  }, [currentTab, dealFilesAvailable, companyCamAvailable])

  // --- Company Cam picker state ---
  const [ccQuery, setCcQuery] = useState('')
  const ccDebouncedQuery = useDebounce(ccQuery, 250)
  const [ccProjects, setCcProjects] = useState<CompanyCamProject[]>([])
  const [ccSelectedProject, setCcSelectedProject] = useState<CompanyCamProject | null>(null)
  const [ccLoadingProjects, setCcLoadingProjects] = useState(false)
  const [ccLoadingFiles, setCcLoadingFiles] = useState(false)
  const [ccOriginalPhotos, setCcOriginalPhotos] = useState<CompanyCamPhotoFile[]>([])
  const [ccAnnotatedPhotos, setCcAnnotatedPhotos] = useState<CompanyCamPhotoFile[]>([])
  // Reopen on the last-used photo tab; default to Original when nothing is
  // stored, since the Annotated tab lists only genuinely marked-up photos (often
  // a small subset or empty) and would otherwise look bare on first open.
  const [ccPhotoTab, setCcPhotoTab] = useState<CompanyCamPhotoTab>(() => readLastPhotoTab(ccUserId) ?? 'original')

  // Persist user-initiated photo-tab switches so the choice survives backing out
  // to the project list and re-opening the dialog later.
  const handleCcPhotoTabChange = useCallback(
    (tab: CompanyCamPhotoTab) => {
      setCcPhotoTab(tab)
      writeLastPhotoTab(ccUserId, tab)
    },
    [ccUserId]
  )
  const [ccSelectedPhotoKeys, setCcSelectedPhotoKeys] = useState<Set<string>>(() => new Set())
  const [ccPage, setCcPage] = useState(1)
  const [ccHasMore, setCcHasMore] = useState(false)
  const [ccCaptionedOnly, setCcCaptionedOnly] = useState(false)
  // Guards the one-shot auto-restore of the last CompanyCam project.
  const ccRestoredRef = useRef(false)
  // Mark the one-shot auto-restore as resolved for this dialog session. Called
  // when a restore fires, when the user manually picks a project, and when they
  // back out — any user navigation must stop the restore effect from re-running
  // and snapping straight back to the remembered project (#21283 follow-up).
  const consumeRestore = useCallback(() => {
    ccRestoredRef.current = true
  }, [])

  const [processEvent] = useMutation<any>(PROCESS_EVENT)

  const searchCcProjects = useCallback(
    async (query: string) => {
      if (currentTab !== 'company-cam' || ccSelectedProject) return
      setCcLoadingProjects(true)
      try {
        const { data } = await processEvent({
          variables: { type: 'get-projects', provider: 'companycam', async: false, params: { query } }
        })
        setCcProjects((data?.processEvent?.result?.result ?? []) as CompanyCamProject[])
      } finally {
        setCcLoadingProjects(false)
      }
    },
    [currentTab, ccSelectedProject, processEvent]
  )

  const fetchCcFiles = useCallback(
    async (projectId: string, page: number) => {
      if (page === 1) setCcLoadingFiles(true)
      try {
        const { data } = await processEvent({
          variables: { type: 'get-files', provider: 'companycam', async: false, params: { projectId, page } }
        })
        const results = (data?.processEvent?.result?.result ?? []) as CompanyCamApiPhoto[]
        setCcHasMore(results.length === 100)
        // Originals = all photos; Annotated = only photos with a real annotation (#21265).
        const { originals: newOriginals, annotated: newAnnotated } = buildCompanyCamPhotoFiles(results)
        if (page === 1) {
          setCcOriginalPhotos(newOriginals)
          setCcAnnotatedPhotos(newAnnotated)
        } else {
          setCcOriginalPhotos((prev) => [...prev, ...newOriginals])
          setCcAnnotatedPhotos((prev) => [...prev, ...newAnnotated])
        }
      } finally {
        if (page === 1) setCcLoadingFiles(false)
      }
    },
    [processEvent]
  )

  // Select a project and remember it for next time the picker opens. A manual
  // pick consumes the auto-restore (see consumeRestore) so a later back-out
  // doesn't re-select this project.
  const selectCcProject = useCallback(
    (project: CompanyCamProject) => {
      consumeRestore()
      setCcSelectedProject(project)
      writeLastCcProject(ccUserId, scopeId, project)
    },
    [ccUserId, scopeId, consumeRestore]
  )

  // On first entering the CompanyCam tab, jump straight to the last-used project
  // (if any). Only a restore that actually fires consumes the flag, so a tab
  // visit with nothing saved doesn't permanently block a later restore.
  useEffect(() => {
    if (currentTab !== 'company-cam' || ccRestoredRef.current || ccSelectedProject) return
    const last = readLastCcProject(ccUserId, scopeId)
    if (last) {
      consumeRestore()
      setCcSelectedProject(last)
    }
  }, [currentTab, ccUserId, scopeId, ccSelectedProject, consumeRestore])

  // Fetch projects when entering the tab or when search changes.
  useEffect(() => {
    if (currentTab === 'company-cam' && !ccSelectedProject) {
      searchCcProjects(ccDebouncedQuery)
    }
  }, [currentTab, ccSelectedProject, ccDebouncedQuery, searchCcProjects])

  // Fetch files when a project is picked.
  useEffect(() => {
    if (ccSelectedProject) {
      setCcPage(1)
      fetchCcFiles(ccSelectedProject.id, 1)
    }
  }, [ccSelectedProject, fetchCcFiles])

  const handleCcBack = useCallback(() => {
    // Backing out consumes the auto-restore so the restore effect can't re-run
    // and snap us straight back to the remembered project (#21283 follow-up).
    consumeRestore()
    setCcSelectedProject(null)
    setCcOriginalPhotos([])
    setCcAnnotatedPhotos([])
    setCcSelectedPhotoKeys(new Set())
    setCcPage(1)
    setCcHasMore(false)
    // Keep the current photo tab so re-entering a project stays on Annotated/Original.
  }, [consumeRestore])

  const handleCcTogglePhoto = (file: CompanyCamPhotoFile) => {
    setCcSelectedPhotoKeys((prev) => {
      const next = new Set(prev)
      if (next.has(file.key)) next.delete(file.key)
      else next.add(file.key)
      return next
    })
  }

  const handleCcSelectAllVisible = () => {
    const base = ccPhotoTab === 'annotated' ? ccAnnotatedPhotos : ccOriginalPhotos
    const visible = ccCaptionedOnly ? base.filter((f) => !!f.caption) : base
    const everySelected = visible.length > 0 && visible.every((f) => ccSelectedPhotoKeys.has(f.key))
    setCcSelectedPhotoKeys((prev) => {
      const next = new Set(prev)
      if (everySelected) visible.forEach((f) => next.delete(f.key))
      else visible.forEach((f) => next.add(f.key))
      return next
    })
  }

  const handleCcLoadMore = async () => {
    if (!ccSelectedProject || !ccHasMore) return
    const nextPage = ccPage + 1
    setCcPage(nextPage)
    await fetchCcFiles(ccSelectedProject.id, nextPage)
  }

  const handleCcConfirm = () => {
    if (!onSelectFiles) return
    // ccSelectedPhotoKeys is a Set (insertion-ordered) so picked files arrive in
    // the order the user clicked them rather than fixed grid order.
    const picked = orderPickedPhotos(ccSelectedPhotoKeys, [...ccAnnotatedPhotos, ...ccOriginalPhotos])
    if (!picked.length) return

    // CompanyCam descriptions arrive programmatically and bypass the editor's
    // `maxLength`. We deliberately keep the full text rather than truncating it —
    // an over-limit caption is instead flagged red and blocked at save time in the
    // file editor so the user shortens it themselves and no text is silently lost
    // (#21295 / ENG-731).
    const files = picked.map(({ _photoId: _id, ...file }) => file)

    onSelectFiles(files)
    setCcSelectedPhotoKeys(new Set())
  }

  const isHeicFile = (file: File) => HEIC_FILE_TYPES.includes(file.type.split('/')[1])

  const handleUploadDrop = (files: File[]) => {
    if (!files.length || !onUpload) return

    let allowedFiles = files
    if (!s3ImageUploadEnabled) {
      allowedFiles = files.filter((file) => !isHeicFile(file))
      if (allowedFiles.length !== files.length) {
        enqueueSnackbar(__('HEIC images are not supported.'), {
          variant: 'error'
        })
      }
    }

    if (!allowedFiles.length) return
    onUpload(allowedFiles)
  }

  const handleToggleSourceFile = (file: QuoteFile) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(file.key)) next.delete(file.key)
      else next.add(file.key)
      return next
    })
  }

  const handleConfirmSelection = () => {
    if (!onSelectFiles) return
    const picked = docFiles.filter((f) => selectedKeys.has(f.key))
    if (!picked.length) return
    onSelectFiles(picked)
    setSelectedKeys(new Set())
  }

  const renderTabLabel = (label: string) => (
    <span className={classes.tabLabel}>
      <span className={classes.availabilityDot} aria-hidden />
      {label}
    </span>
  )

  const renderUploadPanel = () => (
    <div className={classes.panel}>
      <Dropzone
        accept={accept}
        maxSize={maxFileSize}
        multiple
        onDrop={handleUploadDrop}
        className={classes.dropzoneRoot}
        shouldRenderPreview
        renderPreview={() => (
          <div className={classes.dropzonePreview}>
            <CloudUploadOutlinedIcon className={classes.dropzoneIcon} />
            <div className={classes.dropzoneText}>{__('Select or drag and drop')}</div>
          </div>
        )}
      />
      <div className={classes.fileMeta}>{__('Max file size %s', formatFileSize(maxFileSize))}</div>
    </div>
  )

  const renderDealFilesPanel = () => {
    if (!docFiles.length) {
      return (
        <div className={classes.panel}>
          <div className={classes.emptyState}>
            {hasPrefetchedSource
              ? __('No files to pull yet.')
              : __('No files attached to this %s yet.', fileDocType ?? __('record'))}
          </div>
        </div>
      )
    }
    return (
      <div className={classes.panel}>
        <div className={classes.filesGrid}>
          {docFiles.map((file) => {
            const isImage = file.type?.startsWith('image') || file.format?.startsWith('image')
            const isSelected = selectedKeys.has(file.key)
            return (
              <button
                key={file.key}
                type="button"
                className={`${classes.fileTile} ${isSelected ? classes.fileTileSelected : ''}`}
                onClick={() => handleToggleSourceFile(file)}
              >
                {isImage && file.src ? (
                  // Match the new file editor's CORS load so Safari keeps a
                  // single CORS-clean cache entry for this src.
                  <img
                    crossOrigin={imageCrossOrigin(file)}
                    src={file.src}
                    alt={file.title || ''}
                    className={classes.fileTileImg}
                    onError={retryCorsImageOnError}
                  />
                ) : (
                  // PDFs and videos (and any other non-image source) render a real
                  // thumbnail via the shared FilePreview — a PDF's first page or a
                  // Cloudinary video frame — instead of bare filename text (#21654).
                  // Mirrors FilesGridNext. selectedPages={[0]} keeps a multi-page PDF
                  // from stacking every page into the small tile; videoThumbnail draws
                  // a still frame rather than mounting a heavy <video>.
                  <span className={classes.fileTilePreview}>
                    <FilePreview
                      file={file}
                      videoThumbnail
                      selectedPages={isPdfFile(file) ? [0] : undefined}
                      classes={{ root: classes.fileTilePreviewRoot }}
                    />
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  const renderCompanyCamPanel = () => {
    if (!ccSelectedProject) {
      return (
        <div className={classes.ccPanel}>
          <div className={classes.ccSearch}>
            <SearchBar
              classes={{ root: classes.ccSearchBarRoot, paper: classes.ccSearchBarPaper }}
              placeholder={__('Search Projects...')}
              onChange={setCcQuery}
              value={ccQuery}
            />
          </div>
          <div className={classes.ccProjectList}>
            {ccLoadingProjects ? (
              <Spinner padded />
            ) : ccProjects.length === 0 ? (
              <div className={classes.emptyState}>{__('No Company Cam projects found.')}</div>
            ) : (
              ccProjects.map((project) => (
                <ListItem key={project.id} divider button onClick={() => selectCcProject(project)}>
                  {project.featuredImage ? (
                    <img src={project.featuredImage} alt="" className={classes.ccProjectThumb} />
                  ) : (
                    // Projects with no photos have no featured image — render a placeholder
                    // tile so the row stays aligned with projects that have a thumbnail (#21276).
                    <div className={classes.ccProjectThumbPlaceholder} aria-hidden>
                      <ImageIcon className={classes.ccProjectThumbPlaceholderIcon} />
                    </div>
                  )}
                  <ListItemText
                    primary={project.name ?? project.id}
                    secondary={project.address?.street_address_1 ?? ''}
                  />
                </ListItem>
              ))
            )}
          </div>
        </div>
      )
    }

    const visiblePhotos = ccPhotoTab === 'annotated' ? ccAnnotatedPhotos : ccOriginalPhotos
    const displayedPhotos = ccCaptionedOnly ? visiblePhotos.filter((f) => !!f.caption) : visiblePhotos
    const visibleAllSelected =
      displayedPhotos.length > 0 && displayedPhotos.every((f) => ccSelectedPhotoKeys.has(f.key))

    return (
      <div className={classes.ccPanel}>
        <div className={classes.ccProjectHeader}>
          <div className={classes.ccProjectHeaderName}>{ccSelectedProject.name ?? ccSelectedProject.id}</div>
          {ccSelectedProject.address?.street_address_1 && (
            <div className={classes.ccProjectHeaderAddress}>{ccSelectedProject.address.street_address_1}</div>
          )}
        </div>
        <div className={classes.ccSubTabsRow}>
          <Tabs
            value={ccPhotoTab}
            onChange={(_, value) => {
              // Validate rather than blind-cast: never persist an unexpected value
              // (e.g. from a future MUI change) to localStorage.
              if (isCompanyCamPhotoTab(value)) handleCcPhotoTabChange(value)
            }}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab value="annotated" label={__('Annotated')} />
            <Tab value="original" label={__('Original')} />
          </Tabs>
          <div className={classes.ccToolbarActions}>
            <Button
              variant={ccCaptionedOnly ? 'contained' : 'outlined'}
              color={ccCaptionedOnly ? 'primary' : 'default'}
              size="small"
              onClick={() => setCcCaptionedOnly((v) => !v)}
            >
              {__('Captioned only')}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleCcSelectAllVisible}
              disabled={!displayedPhotos.length}
            >
              {visibleAllSelected ? __('Deselect all') : __('Select all')}
            </Button>
          </div>
        </div>
        {ccLoadingFiles ? (
          <Spinner padded />
        ) : (
          <div className={classes.ccPhotoGrid}>
            {displayedPhotos.length === 0 ? (
              <div className={`${classes.emptyState} ${classes.ccPhotoGridEmpty}`}>
                {ccCaptionedOnly
                  ? __('No captioned photos in this project.')
                  : ccPhotoTab === 'annotated'
                    ? __('No annotated photos in this project.')
                    : __('No photos in this project.')}
              </div>
            ) : (
              displayedPhotos.map((file) => {
                const isSelected = ccSelectedPhotoKeys.has(file.key)
                return (
                  <button
                    key={file.key}
                    type="button"
                    className={`${classes.ccPhotoTile} ${isSelected ? classes.ccPhotoTileSelected : ''}`}
                    onClick={() => handleCcTogglePhoto(file)}
                  >
                    {file.src ? (
                      // CompanyCam photos are served from an external host that may
                      // not return CORS headers — keep these non-CORS (no crossOrigin)
                      // to match how provider files are displayed in the viewer.
                      <img src={file.src} alt={file.title ?? ''} className={classes.ccPhotoImg} />
                    ) : (
                      <span className={classes.tileFill}>
                        <FilePreview file={file} />
                      </span>
                    )}
                    {file.caption && (
                      // Show the CompanyCam description while selecting; clamped to
                      // 2 lines with the full text available on hover (#21212). The
                      // clamp lives on the inner span so the container's padding
                      // doesn't clip the last line on iOS Safari (#21231).
                      <span className={classes.ccPhotoCaption} title={file.caption}>
                        <span className={classes.ccPhotoCaptionText}>{file.caption}</span>
                      </span>
                    )}
                  </button>
                )
              })
            )}
            {ccHasMore && (
              <Button variant="outlined" onClick={handleCcLoadMore} fullWidth>
                {__('Load more')}
              </Button>
            )}
          </div>
        )}
      </div>
    )
  }

  const showDealFilesConfirm = currentTab === 'deal-files' && selectedKeys.size > 0
  const showCcPickerFooter = currentTab === 'company-cam' && !!ccSelectedProject

  return (
    <Dialog {...dialogProps} maxWidth="md" fullWidth onClose={onCancel} classes={{ paper: classes.paper }}>
      <DialogTitle
        classes={{ root: classes.dialogTitle }}
        rightContent={
          <IconButton size="small" onClick={onCancel} aria-label={__('Close')}>
            <CloseIcon />
          </IconButton>
        }
      >
        <div className={classes.titleStack}>
          <span>{resolvedTitle}</span>
          {subtitle && <span className={classes.titleSubtitle}>{subtitle}</span>}
        </div>
      </DialogTitle>
      <DialogContent classes={{ root: classes.content }}>
        <Tabs
          value={currentTab}
          onChange={(_, value) => {
            if (isAddFileDialogTab(value)) handleTabChange(value)
          }}
          classes={{ root: classes.tabsRow }}
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab classes={{ root: classes.tab }} value="upload" label={renderTabLabel(__('Upload'))} />
          {dealFilesAvailable && (
            <Tab classes={{ root: classes.tab }} value="deal-files" label={renderTabLabel(resolvedDealFilesLabel)} />
          )}
          {companyCamAvailable && (
            <Tab classes={{ root: classes.tab }} value="company-cam" label={renderTabLabel(__('Company Cam'))} />
          )}
        </Tabs>
        {currentTab === 'upload' && renderUploadPanel()}
        {currentTab === 'deal-files' && renderDealFilesPanel()}
        {currentTab === 'company-cam' && renderCompanyCamPanel()}
      </DialogContent>
      {showDealFilesConfirm && (
        <DialogActions
          leftButton={
            <Button variant="text" onClick={onCancel}>
              {__('Cancel')}
            </Button>
          }
        >
          <Button variant="contained" onClick={handleConfirmSelection}>
            {__('Add %d %s', selectedKeys.size, selectedKeys.size === 1 ? __('file') : __('files'))}
          </Button>
        </DialogActions>
      )}
      {showCcPickerFooter && (
        <DialogActions
          leftButton={
            <Button variant="text" onClick={handleCcBack}>
              {__('Back')}
            </Button>
          }
        >
          <Button variant="contained" onClick={handleCcConfirm} disabled={ccSelectedPhotoKeys.size === 0}>
            {ccSelectedPhotoKeys.size > 0 ? `${__('Confirm')} (${ccSelectedPhotoKeys.size})` : __('Confirm')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  )
}

export default withUser(withClientOptions(AddFileDialog))
